import { useCallback, useMemo, useReducer, useRef } from "react";
import { PlayerState } from "../state/playerState";
import {
  loadPlayerState,
  savePlayerState,
  clearPlayerState,
} from "../state/persistence";

/**
 * React-friendly wrapper around the mutable PlayerState class.
 *
 * The instance itself never changes — useReducer's bump() forces re-renders
 * after any in-place mutation, then auto-saves to localStorage under the
 * tester's per-username key.
 *
 * Returned mutator functions are stable across renders (useCallback) so
 * children that take them as props don't re-render unnecessarily.
 */
export function usePlayerState(username: string) {
  // useRef so the instance survives re-renders.
  const stateRef = useRef<PlayerState | null>(null);
  if (stateRef.current === null) {
    stateRef.current = loadPlayerState(username);
  }
  const state = stateRef.current;

  const [, bump] = useReducer((x: number) => x + 1, 0);

  const persist = useCallback(() => {
    savePlayerState(state, username);
    bump();
  }, [state, username]);

  const addXp = useCallback(
    (amount: number): string[] => {
      const unlocks = state.addXp(amount);
      persist();
      return unlocks;
    },
    [state, persist],
  );

  const deductXp = useCallback(
    (amount: number): void => {
      state.deductXp(amount);
      persist();
    },
    [state, persist],
  );

  const increaseDifficulty = useCallback(
    (modeId: string): void => {
      state.increaseDifficulty(modeId);
      persist();
    },
    [state, persist],
  );

  const setDifficulty = useCallback(
    (modeId: string, difficulty: number): void => {
      state.modeDifficulty[modeId] = Math.max(1, Math.floor(difficulty));
      persist();
    },
    [state, persist],
  );

  const updateHighScore = useCallback(
    (modeId: string, score: number): void => {
      state.updateHighScore(modeId, score);
      persist();
    },
    [state, persist],
  );

  const incrementSessionsPlayed = useCallback((): void => {
    state.sessionsPlayed += 1;
    persist();
  }, [state, persist]);

  const reset = useCallback((): void => {
    state.reset();
    clearPlayerState(username);
    persist();
  }, [state, username, persist]);

  /** Set XP directly. Used by the dev cheat to unlock all modes. */
  const setTotalXp = useCallback(
    (total: number): string[] => {
      const delta = total - state.totalXp;
      if (delta <= 0) {
        // Going down — set directly, no unlock notifications.
        state.totalXp = Math.max(0, total);
        persist();
        return [];
      }
      return addXp(delta);
    },
    [state, persist, addXp],
  );

  // Memoize the returned object so identity is stable per render.
  // (state itself is the same instance forever, so spreading it would alias
  // its mutable properties — caller reads computed properties like state.level
  // which always reflect the latest internal numbers.)
  return useMemo(
    () => ({
      state,
      addXp,
      deductXp,
      increaseDifficulty,
      setDifficulty,
      updateHighScore,
      incrementSessionsPlayed,
      reset,
      setTotalXp,
      /**
       * Force a localStorage save now. Used by GameSession.enterResults
       * — the session mutates PlayerState fields directly (sessionsPlayed,
       * modeHighScores, modeDifficulty) at end-of-session and needs an
       * explicit save because those direct mutations bypass the auto-save
       * wrappers above.
       */
      persist,
    }),
    [
      state,
      addXp,
      deductXp,
      increaseDifficulty,
      setDifficulty,
      updateHighScore,
      incrementSessionsPlayed,
      reset,
      setTotalXp,
      persist,
    ],
  );
}
